/**
 * BulkEmailPage — E-93 Story 93.1 Task 5 integration point.
 *
 * Composes the filter panel, the selectable partner list, and the existing
 * template/language/variables form into a single workflow. Selection
 * survives filter changes (Set-backed hook). Sticky selection counter in
 * the header makes the 500-cap visible at all times.
 */

import type { EligiblePartnerFilters, TemplateLegalBasis } from '@/api/bulk-email';
import { legalBasisForSlug } from '@/api/bulk-email';
import { BulkEmailStatsCards } from '@/components/messaging/BulkEmailStatsCards';
import { ConfirmSendModal } from '@/components/messaging/ConfirmSendModal';
import { LegalBasisBadge } from '@/components/messaging/LegalBasisBadge';
import { PartnerFilterPanel } from '@/components/messaging/PartnerFilterPanel';
import { MAX_SELECTION, PartnerSelectList } from '@/components/messaging/PartnerSelectList';
import { ScheduledBatchList } from '@/components/messaging/ScheduledBatchList';
import { TemplatePreviewModal } from '@/components/messaging/TemplatePreviewModal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useBulkEmailPartners } from '@/hooks/use-bulk-email-partners';
import { useBulkSelection } from '@/hooks/use-bulk-selection';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useDeferredSend } from '@/hooks/use-deferred-send';
import { useDraftAutosave } from '@/hooks/use-draft-autosave';
import { useEmailTemplateList } from '@/pages/settings/email-templates/hooks/useEmailTemplates';
import { useTranslation } from '@kgc/i18n';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@kgc/ui';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';

/**
 * Recipient count above which the Story 93.12 type-to-confirm modal fires.
 * NFR1 caps selection at 500, but the modal triggers much earlier — 100 is
 * where accidental-send damage starts outweighing the UX cost of one extra
 * click. Industry precedent: GitHub bulk label (100), Stripe bulk refund
 * (50). Adjustable, keep constant here for easy tuning.
 */
const CONFIRM_THRESHOLD = 100;

/**
 * Story 93.2 — Fallback slug list for when the DB-backed template list
 * hasn't loaded yet (first paint) or failed. Kept in sync with backend
 * `EMAIL_TEMPLATE_SLUGS` in `@kgc/messaging-gateway/interfaces/email-template`.
 *
 * Runtime reality: the actual templates come from
 * `useEmailTemplateList()` which hits `GET /settings/email-templates` and
 * returns per-tenant customized `{ slug, name, subject, ... }` rows. This
 * fallback only exists so the Select isn't empty while the query resolves.
 */
const FALLBACK_TEMPLATE_SLUGS = [
  'invoice-sent',
  'invoice-overdue',
  'rental-confirmation',
  'rental-overdue',
  'rental-deposit-warning',
  'worksheet-created',
  'worksheet-ready',
  'quote-sent',
  'welcome-partner',
  'payment-received',
] as const;

/**
 * Story 93.10 — Gmail-style deferred send window. The POST stays un-fired
 * for this many ms after the user clicks Send, giving them time to hit
 * Undo in the inline banner. 5s matches the Sally UX decision; Gmail's
 * range is 5-30s, we pick the fast end so the wait isn't annoying.
 */
const UNDO_WINDOW_MS = 5000;

type Language = 'hu' | 'en' | 'de' | 'uk';

interface BulkSendResult {
  queued: number;
  skipped: number;
  failed: number;
  legalBasis?: TemplateLegalBasis;
  skippedDetails?: Array<{
    partnerId: string;
    reason: 'no_email' | 'not_found' | 'no_consent' | 'unsubscribed';
  }>;
}

const INITIAL_FILTERS: EligiblePartnerFilters = { limit: 50 };

/**
 * Story 93.11 — Persistable slice of the page state. We intentionally
 * exclude `selection` (large Set that's stale once the partner list
 * refreshes) and any transient UI flags. The user's compositional work
 * — which template, which language, which custom variables — survives
 * a browser crash or tab close.
 */
interface BulkEmailDraft {
  templateSlug: string;
  language: Language;
  variables: { key: string; value: string }[];
}

const LANGS_SET: readonly Language[] = ['hu', 'en', 'de', 'uk'] as const;

function isBulkEmailDraft(value: unknown): value is BulkEmailDraft {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<BulkEmailDraft>;
  return (
    typeof v.templateSlug === 'string' &&
    typeof v.language === 'string' &&
    LANGS_SET.includes(v.language as Language) &&
    Array.isArray(v.variables)
  );
}

export function BulkEmailPage(): JSX.Element {
  const { t } = useTranslation();

  // Filter state. `searchInput` is the raw input box value; `filters` holds
  // everything that actually drives the query (including the debounced
  // search term). The two-way split is what makes typing feel instant
  // while still respecting the 300ms debounce.
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 300);
  const [filters, setFilters] = useState<EligiblePartnerFilters>(INITIAL_FILTERS);

  const queryFilters = useMemo<EligiblePartnerFilters>(
    () => ({
      ...filters,
      search: debouncedSearch.length > 0 ? debouncedSearch : undefined,
    }),
    [filters, debouncedSearch]
  );

  const { data, isLoading, isFetching, error, refetch } = useBulkEmailPartners(queryFilters);
  const selection = useBulkSelection();

  const partners = data?.data ?? [];
  const totalCount = data?.totalCount ?? 0;
  const hasMore = data?.hasMore ?? false;

  const [templateSlug, setTemplateSlug] = useState<string>('');
  const [language, setLanguage] = useState<Language>('hu');

  // Story 93.2 — DB-backed template list. The backend returns per-tenant
  // customized { slug, name, subject } rows. We filter to isActive so
  // disabled templates don't appear in the picker.
  const { data: templateList } = useEmailTemplateList(language);
  const activeTemplates = useMemo(
    () => (templateList ?? []).filter(t => t.isActive),
    [templateList]
  );

  // Display-name resolver. DB.name has precedence; slug is the stable fallback.
  const templateDisplayName = useMemo(() => {
    if (!templateSlug) return '';
    const entry = activeTemplates.find(t => t.slug === templateSlug);
    return entry?.name ?? templateSlug;
  }, [templateSlug, activeTemplates]);
  const [variables, setVariables] = useState<{ key: string; value: string }[]>([]);
  const [result, setResult] = useState<BulkSendResult | null>(null);
  const [sendLoading, setSendLoading] = useState(false);

  // Story 93.12 — type-to-confirm modal state. `confirmCount` is snapshotted
  // at the moment the modal opens so a background selection change cannot
  // retarget the confirm requirement mid-flow (adversarial race guard).
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmCount, setConfirmCount] = useState(0);

  // Story 93.4 — template preview modal.
  const [previewOpen, setPreviewOpen] = useState(false);

  // Story 93.10 — 5s deferred-send (Gmail-style undo window).
  const deferredSend = useDeferredSend();

  // Story 93.11 — 30s localStorage autosave. Key is per-page for now;
  // when multi-tenant login lands in kgc-web the key should include a
  // tenantId suffix to avoid cross-tenant draft contamination.
  const draftState = useMemo<BulkEmailDraft>(
    () => ({ templateSlug, language, variables }),
    [templateSlug, language, variables]
  );
  const { loadedDraft, clearDraft } = useDraftAutosave({
    key: 'bulk-email-draft',
    data: draftState,
    validate: isBulkEmailDraft,
  });
  const [restoreDismissed, setRestoreDismissed] = useState(false);
  const showRestoreBanner = loadedDraft !== null && !restoreDismissed;

  function handleRestoreDraft(): void {
    if (!loadedDraft) return;
    setTemplateSlug(loadedDraft.data.templateSlug);
    setLanguage(loadedDraft.data.language);
    setVariables(loadedDraft.data.variables);
    setRestoreDismissed(true);
  }

  function handleDismissDraft(): void {
    clearDraft();
    setRestoreDismissed(true);
  }

  function addVariable(): void {
    setVariables(prev => [...prev, { key: '', value: '' }]);
  }
  function updateVariable(index: number, field: 'key' | 'value', val: string): void {
    setVariables(prev => prev.map((v, i) => (i === index ? { ...v, [field]: val } : v)));
  }
  function removeVariable(index: number): void {
    setVariables(prev => prev.filter((_, i) => i !== index));
  }

  function handleLoadMore(): void {
    if (!data || !data.nextCursor) return;
    setFilters(prev => ({ ...prev, cursor: data.nextCursor ?? undefined }));
  }

  function handleMaxReached(): void {
    toast.warning(t('email.bulkSend.filters.maxSelectionReached'));
  }

  function handleSendRequest(): void {
    // Front-load the cheap validation errors so the confirm modal never
    // opens for a send that would fail anyway.
    if (!templateSlug) {
      toast.error(t('email.bulkSend.errorNoTemplate'));
      return;
    }
    if (selection.selectedCount === 0) {
      toast.error(t('email.bulkSend.errorNoRecipients'));
      return;
    }
    if (selection.selectedCount >= CONFIRM_THRESHOLD) {
      setConfirmCount(selection.selectedCount);
      setConfirmOpen(true);
      return;
    }
    // Story 93.10 — small-count sends also get the 5s undo window. The
    // consistency matters: a user who misclicks on 20 recipients is just
    // as surprised as one who misclicks on 200.
    deferredSend.scheduleSend(() => handleSend(), UNDO_WINDOW_MS);
  }

  function handleConfirmSend(): void {
    setConfirmOpen(false);
    deferredSend.scheduleSend(() => handleSend(), UNDO_WINDOW_MS);
  }

  function handleUndo(): void {
    deferredSend.cancelSend();
    toast.success(t('email.bulkSend.undo.undoneToast'));
  }

  async function handleSend(): Promise<void> {
    if (!templateSlug) {
      toast.error(t('email.bulkSend.errorNoTemplate'));
      return;
    }
    const partnerIds = Array.from(selection.selectedIds);
    if (partnerIds.length === 0) {
      toast.error(t('email.bulkSend.errorNoRecipients'));
      return;
    }
    setSendLoading(true);
    setResult(null);
    try {
      const payload = {
        templateSlug,
        language,
        partnerIds,
        variables: Object.fromEntries(variables.filter(v => v.key).map(v => [v.key, v.value])),
      };
      const res = await fetch('/api/v1/email/bulk-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status.toString()}`);
      const body = (await res.json()) as { data: BulkSendResult };
      setResult(body.data);
      selection.clearSelection();
      // Story 93.11 — a successfully sent campaign should NOT be offered
      // back as a restorable draft on the next page load.
      clearDraft();
      toast.success(t('email.bulkSend.success'));
      void refetch();
    } catch {
      toast.error(t('email.bulkSend.errorSend'));
    } finally {
      setSendLoading(false);
    }
  }

  const LANGS: Language[] = ['hu', 'en', 'de', 'uk'];

  return (
    <div className="container mx-auto max-w-6xl py-8 space-y-6">
      <div className="sticky top-0 z-20 -mx-4 flex items-center justify-between border-b bg-background px-4 py-2 backdrop-blur">
        <h1 className="text-2xl font-bold">{t('email.bulkSend.title')}</h1>
        <div className="text-sm font-medium" data-testid="selection-counter">
          {t('email.bulkSend.filters.selectedCount', {
            count: selection.selectedCount,
            max: MAX_SELECTION,
          })}
        </div>
      </div>

      {showRestoreBanner && (
        <div
          role="status"
          data-testid="draft-restore-banner"
          className="flex items-center justify-between gap-4 rounded-md border border-blue-300 bg-blue-50 px-4 py-3 text-sm dark:border-blue-700 dark:bg-blue-950/40"
        >
          <div>
            <p className="font-medium text-blue-900 dark:text-blue-100">
              {t('email.bulkSend.draft.restoreTitle')}
            </p>
            <p className="text-xs text-blue-900/70 dark:text-blue-100/70">
              {t('email.bulkSend.draft.restoreSubtitle', {
                savedAt: new Date(loadedDraft?.savedAt ?? 0).toLocaleString(),
              })}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleRestoreDraft}>
              {t('email.bulkSend.draft.restoreButton')}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleDismissDraft}>
              {t('email.bulkSend.draft.dismissButton')}
            </Button>
          </div>
        </div>
      )}

      <BulkEmailStatsCards />

      <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
        <div className="space-y-4">
          <PartnerFilterPanel filters={filters} onFilterChange={setFilters} />

          <div className="space-y-1">
            <label className="text-sm font-medium">{t('email.bulkSend.filters.search')}</label>
            <Input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder={t('email.bulkSend.filters.search')}
              aria-label={t('email.bulkSend.filters.search')}
            />
          </div>

          {totalCount > MAX_SELECTION && (
            <p className="rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
              {t('email.bulkSend.filters.maxLimitWarning', { max: MAX_SELECTION })}
            </p>
          )}
        </div>

        <div className="space-y-4">
          {isLoading ? (
            <div className="rounded-md border p-6 text-center text-muted-foreground">
              {t('email.bulkSend.filters.loading')}
            </div>
          ) : error ? (
            <div className="rounded-md border border-destructive p-4 text-sm text-destructive">
              {error}
            </div>
          ) : (
            <PartnerSelectList
              partners={partners}
              totalCount={totalCount}
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              onMaxReached={handleMaxReached}
              selection={selection}
              isFetching={isFetching}
            />
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('email.bulkSend.settings')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">{t('email.bulkSend.template')}</label>
              <div className="flex items-center gap-2">
                {templateSlug && <LegalBasisBadge legalBasis={legalBasisForSlug(templateSlug)} />}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPreviewOpen(true)}
                  disabled={!templateSlug}
                  aria-label={t('email.bulkSend.preview.openButton')}
                >
                  {t('email.bulkSend.preview.openButton')}
                </Button>
              </div>
            </div>
            <Select value={templateSlug} onValueChange={setTemplateSlug}>
              <SelectTrigger>
                <SelectValue placeholder={t('email.bulkSend.templatePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {activeTemplates.length > 0
                  ? activeTemplates.map(entry => (
                      <SelectItem key={entry.slug} value={entry.slug}>
                        {entry.name}
                      </SelectItem>
                    ))
                  : FALLBACK_TEMPLATE_SLUGS.map(slug => (
                      <SelectItem key={slug} value={slug}>
                        {slug}
                      </SelectItem>
                    ))}
              </SelectContent>
            </Select>
            {templateSlug && (
              <p className="pt-1 text-xs text-muted-foreground">
                {activeTemplates.find(tpl => tpl.slug === templateSlug)?.subject ?? null}
              </p>
            )}
            {legalBasisForSlug(templateSlug) === 'MARKETING_CONSENT' && (
              <p
                className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
                data-testid="marketing-consent-warning"
              >
                {t('email.bulkSend.legalBasis.marketingWarning')}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">{t('email.bulkSend.language')}</label>
            <div className="flex gap-2">
              {LANGS.map(lang => (
                <Button
                  key={lang}
                  variant={language === lang ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setLanguage(lang)}
                >
                  {lang.toUpperCase()}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">{t('email.bulkSend.variables')}</label>
              <Button variant="outline" size="sm" onClick={addVariable}>
                {t('email.bulkSend.addVariable')}
              </Button>
            </div>
            {variables.map((v, i) => (
              <div key={i} className="flex gap-2">
                <Input
                  placeholder={t('email.bulkSend.variableKey')}
                  value={v.key}
                  onChange={e => updateVariable(i, 'key', e.target.value)}
                />
                <Input
                  placeholder={t('email.bulkSend.variableValue')}
                  value={v.value}
                  onChange={e => updateVariable(i, 'value', e.target.value)}
                />
                <Button variant="ghost" size="sm" onClick={() => removeVariable(i)}>
                  ×
                </Button>
              </div>
            ))}
          </div>

          <Button
            onClick={handleSendRequest}
            disabled={sendLoading || deferredSend.isPending || selection.selectedCount === 0}
            className="w-full"
          >
            {sendLoading
              ? t('email.bulkSend.sending')
              : deferredSend.isPending
                ? t('email.bulkSend.undo.sendingNow')
                : templateDisplayName
                  ? t('email.bulkSend.sendDynamic', {
                      template: templateDisplayName,
                      count: selection.selectedCount,
                    })
                  : t('email.bulkSend.filters.sendWithCount', { count: selection.selectedCount })}
          </Button>
        </CardContent>
      </Card>

      {deferredSend.isPending && (
        <div
          role="alert"
          className="sticky bottom-4 mx-auto flex max-w-xl items-center justify-between gap-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm shadow dark:border-amber-700 dark:bg-amber-950/40"
          data-testid="undo-banner"
        >
          <div className="space-y-0.5">
            <p className="font-medium text-amber-900 dark:text-amber-100">
              {t('email.bulkSend.undo.toastTitle', { seconds: deferredSend.remainingSeconds })}
            </p>
            <p className="text-xs text-amber-900/70 dark:text-amber-100/70">
              {t('email.bulkSend.undo.toastBody', { count: selection.selectedCount })}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={handleUndo}>
            {t('email.bulkSend.undo.undoButton')}
          </Button>
        </div>
      )}

      {result !== null && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('email.bulkSend.results')}</span>
              {result.legalBasis && <LegalBasisBadge legalBasis={result.legalBasis} />}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-emerald-600">{result.queued}</p>
                <p className="text-sm text-muted-foreground">{t('email.bulkSend.queued')}</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{result.skipped}</p>
                <p className="text-sm text-muted-foreground">{t('email.bulkSend.skipped')}</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-destructive">{result.failed}</p>
                <p className="text-sm text-muted-foreground">{t('email.bulkSend.failed')}</p>
              </div>
            </div>
            {result.skippedDetails && result.skippedDetails.length > 0 && (
              <div className="border-t pt-3 text-xs">
                <p className="mb-2 font-medium text-muted-foreground">
                  {t('email.bulkSend.skipBreakdown')}
                </p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {(['no_email', 'not_found', 'unsubscribed', 'no_consent'] as const).map(
                    reason => {
                      const count =
                        result.skippedDetails?.filter(s => s.reason === reason).length ?? 0;
                      if (count === 0) return null;
                      return (
                        <div
                          key={reason}
                          className="rounded-md border bg-muted/40 p-2"
                          data-testid={`skip-count-${reason}`}
                        >
                          <p className="text-lg font-semibold">{count}</p>
                          <p className="text-muted-foreground">
                            {t(`email.bulkSend.skipReason.${reason}`)}
                          </p>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <ConfirmSendModal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        count={confirmCount}
        onConfirm={handleConfirmSend}
      />

      <TemplatePreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        slug={templateSlug || null}
        language={language}
        variables={Object.fromEntries(variables.filter(v => v.key).map(v => [v.key, v.value]))}
      />

      {/*
       * E-93 Story 93.6 — ütemezett batch lista a page alján.
       * Auto-poll 30s; cancel modal a komponensen belül.
       */}
      <ScheduledBatchList />
    </div>
  );
}
